import { useId, type CSSProperties } from 'react';
import styles from './AspectRatioIcon.module.css';

const SVG_RATIOS = [
  '1:1', '16:9', '9:16', '1.91:1', '1:1.91', '3:2', '2:3',
  '728:90', '320:50', '4:3', '3:4', '4:5', '6:5', '4:1', '1:4', '8:1', '1:8',
];

function parseRatio(ratio: string): number | null {
  if (!/^\d+(?:\.\d+)?\s*:\s*\d+(?:\.\d+)?$/.test(ratio.trim())) return null;
  const [w, h] = ratio.split(':').map(Number);
  const value = w / h;
  return w > 0 && h > 0 && Number.isFinite(value) && value > 0 ? value : null;
}

type Props = {
  ratio: string;
  size?: number;
  className?: string;
  style?: CSSProperties;
};

/** 同値の比率は共通SVGを使用し、未収録の比率はその場で描画する。生成設定は変更しない。 */
export function AspectRatioIcon({ ratio, size, className, style }: Props) {
  const clipId = `aspect-${useId().replace(/:/g, '')}`;
  const dimensions = size === undefined ? {} : { width: size, height: size };
  const value = parseRatio(ratio);
  // 1.2:1 / 300:250 も 6:5 のSVGに対応させる。近似値への丸めはしない。
  const svgRatio = value === null ? undefined : SVG_RATIOS.find((candidate) => {
    const candidateValue = parseRatio(candidate)!;
    return Math.abs(value - candidateValue) <= candidateValue * 1e-10;
  });
  if (svgRatio) {
    const url = `url("/aspect_${svgRatio.replaceAll('.', '').replace(':', 'x')}.svg")`;
    return (
      <span
        className={`${styles.icon} ${className ?? ''}`}
        style={{ ...dimensions, ...style, WebkitMaskImage: url, maskImage: url }}
        role="img"
        aria-label={svgRatio}
      />
    );
  }
  if (value === null) {
    return <span className={`${styles.fallback} ${className ?? ''}`} style={{ ...dimensions, ...style }} role="img" aria-label={ratio}>{ratio}</span>;
  }

  const width = value >= 1 ? 22 : 22 * value;
  const height = value >= 1 ? 22 / value : 22;
  const x = (24 - width) / 2;
  const y = (24 - height) / 2;
  const fontSize = ratio.length <= 4 ? 6.5 : ratio.length <= 6 ? 5 : 4;
  const textWidth = Math.min(19, ratio.length * fontSize * 0.6);
  const gapX = (24 - textWidth) / 2 - 1;
  const gapY = (24 - fontSize) / 2 - 1.5;
  return (
    <span className={`${styles.drawing} ${className ?? ''}`} style={{ ...dimensions, ...style }} role="img" aria-label={ratio}>
      <svg width="100%" height="100%" viewBox="0 0 24 24" aria-hidden="true">
        <defs>
          <clipPath id={clipId}>
            {/* 数字領域だけ切り抜き、背景色に依存せず枠を途切れさせる。 */}
            <path clipRule="evenodd" d={`M0 0H24V24H0Z M${gapX} ${gapY}H${24 - gapX}V${24 - gapY}H${gapX}Z`} />
          </clipPath>
        </defs>
        <rect x={x} y={y} width={width} height={height} rx={Math.min(1.5, width / 5, height / 5)} fill="none" stroke="currentColor" strokeWidth={1} clipPath={`url(#${clipId})`} />
        <text x={12} y={12} textAnchor="middle" dominantBaseline="central" fill="currentColor" fontFamily="system-ui, sans-serif" fontSize={fontSize} fontWeight={600} textLength={textWidth} lengthAdjust="spacingAndGlyphs">{ratio}</text>
      </svg>
    </span>
  );
}
